import { memo, useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiCardList,
  MiList,
  MiMenu,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
  PERMISSIONS,
} from "@miplatform/constants";
import { formatDate, getCurrencyString } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Typography, useMediaQuery } from "@mui/material";
import { Box } from "@mui/system";
import {
  ACCOUNTING_DEFAULT_FIELD_WIDTH,
  BillingCommitmentLabelMap,
  INVOICING_ROUTES,
} from "../../../accountingConstants";
import { formatOpenBalanceDisplay } from "../../reconcileConstants";

export const PurchaseOrderList = memo(
  ({
    orderTemplate = {},
    order,
    isReviewMode,
    viewMode,
    onClick,
    onEdit,
    onDelete,
    onVoid,
  }) => {
    const { purchaseOrders = [], amount = 0, openBalance = 0 } = orderTemplate;

    const { hasPermission } = usePermissions();

    const { classes, theme } = useStyles();
    const canUpdate = hasPermission(PERMISSIONS.BILLING_COMMITMENT.UPDATE);
    const canVoid = hasPermission(PERMISSIONS.BILLING_COMMITMENT.VOID);
    const canDelete = hasPermission(PERMISSIONS.BILLING_COMMITMENT.DELETE);

    const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));

    const sections = [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "name",
            maxWidth: 500,
            justifyContent: "flex-end",
            renderCell: ({
              modifiedOn,
              amount,
              type,
              openBalance,
              voidDate,
            }) => {
              return (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  gap={theme.spacing(2)}
                >
                  <MiStackedTypography
                    title="Date Added"
                    className={classes.fieldMargin}
                    titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                    description={formatDate(
                      modifiedOn,
                      DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                    )}
                    descriptionProps={{
                      width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                    }}
                  />
                  <MiStackedTypography
                    title="Payment Method"
                    className={classes.fieldMargin}
                    titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                    description={BillingCommitmentLabelMap[type] || "Unknown"}
                    descriptionProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                  />
                  <MiStackedTypography
                    title="Amount"
                    className={classes.fieldMargin}
                    titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                    description={getCurrencyString(amount)}
                    descriptionProps={{
                      width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                      style: {
                        textDecoration: voidDate ? "line-through" : "none",
                      },
                    }}
                  />
                  <MiStackedTypography
                    title="Open Balance"
                    className={classes.fieldMargin}
                    titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                    description={
                      openBalance < 0
                        ? formatOpenBalanceDisplay(openBalance)
                        : formatOpenBalanceDisplay(0)
                    }
                    descriptionProps={{
                      width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                      style: {
                        color:
                          openBalance < 0 ? theme.palette.red.main : "inherit",
                        textDecoration: voidDate ? "line-through" : "none",
                      },
                    }}
                  />
                  <MiStackedTypography
                    title="Excess Balance"
                    className={classes.fieldMargin}
                    titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                    description={
                      openBalance > 0
                        ? formatOpenBalanceDisplay(openBalance)
                        : formatOpenBalanceDisplay(0)
                    }
                    descriptionProps={{
                      width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                      style: {
                        color:
                          openBalance > 0
                            ? theme.palette.green.main
                            : "inherit",
                        textDecoration: voidDate ? "line-through" : "none",
                      },
                    }}
                  />
                </Box>
              );
            },
          },
        ],
      },
    ];

    const renderTitle = (item) => (
      <MiStackedTypography
        title={
          item.voidDate ? (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography
                fontWeight="bold"
                maxWidth={190}
                noWrap
                color="primary.main"
              >
                {item.name}
              </Typography>
              <Box
                bgcolor="#F44336"
                border="1px solid #D32F2F"
                px={1}
                borderRadius={1}
              >
                <Typography color="white" fontSize={11}>
                  Voided :{" "}
                  {formatDate(item.voidDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)}
                </Typography>
              </Box>
            </Box>
          ) : (
            item.name
          )
        }
        description={`#${item?.number}`}
        titleProps={{
          className: classes.fieldDescription,
          color: "primary.main",
          width: 300,
        }}
        descriptionProps={{
          width: 300,
          color: "text.secondary",
          sx: { textDecoration: item.voidDate ? "line-through" : "none" },
        }}
      />
    );

    const renderItemActions = (item) => (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu
          data={[
            {
              label: BUTTON_LABELS.EDIT,
              icon: mdi[ICONS_WEB.PENCIL_OUTLINE],
              hide: !canUpdate || viewMode === INVOICING_ROUTES.INVOICED,
              action: () => onEdit(item),
            },
            {
              label: BUTTON_LABELS.VOID,
              icon: mdi[ICONS_WEB.CANCEL],
              hide: !canVoid,
              action: () => onVoid(item),
            },
            {
              label: BUTTON_LABELS.DELETE,
              icon: mdi[ICONS_WEB.TRASH_CAN],
              hide: !canDelete || viewMode === INVOICING_ROUTES.INVOICED,
              action: () => onDelete(item),
              color: theme.palette.red.main,
            },
          ]}
        />
      </Box>
    );

    const PurchaseOrderCard = useCallback(
      ({ item }) => {
        const orderIds = item.invoices.map((invoice) => invoice.orderId);

        let backgroundColor = theme.palette.background.paper;

        if (orderIds.includes(order.id)) {
          backgroundColor = theme.palette.success.light;
        } else if (item.openBalance > 0) {
          backgroundColor = theme.palette.background.paper;
        } else {
          backgroundColor = theme.palette.grey.lightAccent;
        }

        return (
          <MiCardList
            testID="invoicing-payment-card"
            item={item}
            cardListContainerProps={{
              style: { padding: 0, marginTop: theme.spacing(2) },
            }}
            sections={sections}
            leftCardProps={{
              leftWidth: isSmallScreen ? 200 : "inherit",
              leftMaxWidth: isSmallScreen ? 200 : 300,
            }}
            renderTitle={renderTitle}
            renderItemActions={(item) =>
              (canUpdate || canDelete) && !isReviewMode && !item.voidDate ? (
                renderItemActions(item)
              ) : (
                <Box width={24} />
              )
            }
            onClick={onClick}
            containerStyles={{
              backgroundColor,
            }}
          />
        );
      },
      [
        purchaseOrders,
        order,
        canUpdate,
        canDelete,
        isReviewMode,
        theme,
        isSmallScreen,
      ]
    );

    return (
      <>
        <Box
          display="flex"
          gap={4}
          p={2}
          pt={1}
          data-testid="invoicing-payment-documents"
        >
          <MiStackedTypography
            title="Payment Information"
            description={purchaseOrders?.length}
            descriptionProps={{
              className: classes.fieldDescription,
            }}
          />
          <MiStackedTypography
            title="Total Amount"
            description={getCurrencyString(amount)}
            descriptionProps={{
              className: classes.fieldDescription,
            }}
          />
          <MiStackedTypography
            title="Total Open Balance"
            description={
              openBalance < 0
                ? formatOpenBalanceDisplay(openBalance)
                : formatOpenBalanceDisplay(0)
            }
            descriptionProps={{
              className: classes.fieldDescription,
              color: openBalance < 0 ? "red.main" : "inherit",
            }}
          />
          <MiStackedTypography
            title="Total Excess Balance"
            description={
              openBalance > 0
                ? formatOpenBalanceDisplay(openBalance)
                : formatOpenBalanceDisplay(0)
            }
            descriptionProps={{
              className: classes.fieldDescription,
              color: openBalance > 0 ? "green.main" : "inherit",
            }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          sx={{ overflow: "hidden" }}
        >
          <MiList
            hideListPagination
            viewType="card"
            CardComponent={PurchaseOrderCard}
            data={purchaseOrders}
          />
        </Box>
      </>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  fieldDescription: {
    fontWeight: theme.fontWeight.bold,
  },
  fieldMargin: {
    marginRight: theme.spacing(2),
  },
}));
